import { ChangeEvent, FormEvent, Fragment, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

import { createHealthPromotionImport } from '../services/healthPromotionImportsService';
import type { HealthPromotionAuditItem, HealthPromotionRecord } from '../services/healthPromotionTypes';
import { parseCurrencyValue, parseHealthPromotionFile } from '../services/healthPromotionParser';
import { isCompetenceConsolidated } from '../services/expensesConsolidationService';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { normalizeString } from '@/utils/stringUtils';

type CollaboratorReference = {
  id: string;
  name: string;
  cpf?: string | null;
  normalizedName: string;
  emails: string[];
  normalizedEmails: string[];
  isActive: boolean;
};

type HealthPromotionMatchStatus = 'auto_email' | 'manual' | 'not_found';

type HealthPromotionMatch = {
  record: HealthPromotionRecord;
  collaboratorId: string | null;
  status: HealthPromotionMatchStatus;
};

const OPERATOR = {
  name: 'Wellhub',
  slug: 'wellhub',
};

const normalizeEmailValue = (value?: string | null): string => value?.trim().toLowerCase() ?? '';

const formatCurrency = (value: number): string => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const inferBillingPeriod = (value?: string | null): string | null => value || null;

function MappingCombobox({
  value,
  options,
  disabled,
  onChange,
}: {
  value?: string | null;
  options: CollaboratorReference[];
  disabled?: boolean;
  onChange: (id: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const selected = options.find((option) => option.id === value) ?? null;
  const filtered = useMemo(() => {
    const normalizedQuery = normalizeString(deferredQuery);
    if (!normalizedQuery) return options;
    return options.filter(
      (option) =>
        option.normalizedName.includes(normalizedQuery) ||
        option.normalizedEmails.some((email) => email.includes(normalizedQuery)),
    );
  }, [options, deferredQuery]);

  return (
    <Combobox value={selected} onChange={(option) => onChange(option?.id ?? null)} disabled={disabled}>
      {({ open }) => (
        <div className="relative w-full min-w-[16rem]">
          <Combobox.Input
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            displayValue={(option: CollaboratorReference | null) => option?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder="Selecionar colaborador..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-500">Nenhum resultado</div>
              ) : (
                filtered.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={person}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span className="block">
                          <span className={`block ${isSelected ? 'font-semibold text-gray-900' : 'font-normal text-gray-900'}`}>
                            {person.name}
                          </span>
                          {person.emails.length ? (
                            <span className="block text-xs text-gray-500">{person.emails[0]}</span>
                          ) : null}
                          {person.cpf ? (
                            <span className="block text-xs text-gray-500">
                              {person.cpf.replace?.(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') ?? person.cpf}
                            </span>
                          ) : null}
                        </span>
                        {isSelected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

function StatusBadge({ status }: { status: HealthPromotionMatchStatus }) {
  const statusMap: Record<HealthPromotionMatchStatus, { text: string; className: string }> = {
    auto_email: { text: 'Auto (e-mail)', className: 'bg-green-100 text-green-700' },
    manual: { text: 'Manual', className: 'bg-blue-100 text-blue-700' },
    not_found: { text: 'Sem match', className: 'bg-amber-100 text-amber-700' },
  };
  const data = statusMap[status];
  const Icon = status === 'not_found' ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${data.className}`}>
      <Icon className="h-4 w-4" aria-hidden />
      {data.text}
    </span>
  );
}

export function HealthPromotionImportPage() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const { user } = useAuth();
  const { data: collaborators, isLoading: loadingCollaborators } = useCollaboratorsRealtimeQuery();

  const [billingPeriod, setBillingPeriod] = useState('');
  const [invoiceValue, setInvoiceValue] = useState('');
  const [perSeatAmount, setPerSeatAmount] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [matches, setMatches] = useState<HealthPromotionMatch[]>([]);
  const [totals, setTotals] = useState({ dependents: 0, feeCount: 0, amount: 0 });
  const [parseError, setParseError] = useState<string | null>(null);

  const collaboratorRefs = useMemo<CollaboratorReference[]>(() => {
    return (collaborators || []).map((collaborator) => {
      const name = collaborator.name ?? 'Sem nome';
      const arrayEmails = Array.isArray((collaborator as any).emails) ? ((collaborator as any).emails as string[]) : [];
      const rawEmails = [
        collaborator.email,
        (collaborator as any).personalEmail,
        (collaborator as any).corporateEmail,
        (collaborator as any).companyEmail,
        (collaborator as any).workEmail,
        (collaborator as any).professionalEmail,
        (collaborator as any).contactEmail,
        ...arrayEmails,
      ];
      const emails = rawEmails
        .map((emailValue) => (typeof emailValue === 'string' ? emailValue.trim() : ''))
        .filter((emailValue, index, array) => emailValue && array.indexOf(emailValue) === index);
      const normalizedEmails = emails.map((email) => email.toLowerCase());
      return {
        id: collaborator.id ?? '',
        name,
        cpf: (collaborator as any).cpf ?? null,
        normalizedName: normalizeString(name),
        emails,
        normalizedEmails,
        isActive: (collaborator as any).status !== 'desligado' && (collaborator as any).status !== 'inactive',
      };
    });
  }, [collaborators]);

  const collaboratorByEmail = useMemo(() => {
    const map = new Map<string, CollaboratorReference>();
    collaboratorRefs.forEach((collab) => {
      collab.normalizedEmails.forEach((email) => {
        if (email && !map.has(email)) {
          map.set(email, collab);
        }
      });
    });
    return map;
  }, [collaboratorRefs]);

  const sourceFiles = useMemo(
    () =>
      files.map((file) => ({
        name: file.name,
        size: file.size,
        contentType: file.type || null,
      })),
    [files],
  );

  useEffect(() => {
    const seatCount = matches.length;
    if (!seatCount) return;
    const totalInput = parseCurrencyValue(invoiceValue);
    if (!totalInput) return;
    const perSeat = totalInput / seatCount;
    setMatches((prev) =>
      prev.map((match) => ({
        ...match,
        record: { ...match.record, amount: perSeat },
      })),
    );
    setPerSeatAmount(perSeat);
    setTotals({ dependents: 0, feeCount: seatCount, amount: perSeat * seatCount });
  }, [invoiceValue, matches.length]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files ? Array.from(event.target.files) : [];
    setFiles(fileList);
    setMatches([]);
    setTotals({ dependents: 0, feeCount: 0, amount: 0 });
    setPerSeatAmount(0);
    setParseError(null);
  };

  const handleParse = async () => {
    if (!files.length) {
      setParseError('Selecione o arquivo exportado pela Wellhub para continuar.');
      return;
    }
    setParsing(true);
    setParseError(null);
    try {
      const result = await parseHealthPromotionFile(files[0]!);
      const seatCount = result.records.length;
      if (!seatCount) {
        setParseError('O arquivo não possui registros ativos para o período.');
        setMatches([]);
        setTotals({ dependents: 0, feeCount: 0, amount: 0 });
        setPerSeatAmount(0);
        return;
      }
      const totalInput = parseCurrencyValue(invoiceValue);
      const perSeat = totalInput && seatCount ? totalInput / seatCount : result.records[0]?.amount ?? 0;
      const normalizedMatches: HealthPromotionMatch[] = result.records.map((record) => {
        const emailKey = normalizeEmailValue(record.email);
        const collaborator = collaboratorByEmail.get(emailKey);
        return {
          record: { ...record, amount: perSeat },
          collaboratorId: collaborator?.id ?? null,
          status: collaborator ? 'auto_email' : 'not_found',
        };
      });
      setMatches(normalizedMatches);
      setPerSeatAmount(perSeat);
      setTotals({ dependents: 0, feeCount: seatCount, amount: perSeat * seatCount });
      if (!billingPeriod) {
        const inferred = inferBillingPeriod(result.records[0]?.referenceMonth);
        if (inferred) {
          setBillingPeriod(inferred);
        }
      }
    } catch (error) {
      console.error('[HealthPromotionImportPage] parse error', error);
      setMatches([]);
      setTotals({ dependents: 0, feeCount: 0, amount: 0 });
      setPerSeatAmount(0);
      setParseError('Não foi possível ler o arquivo. Confirme se é o relatório oficial da Wellhub.');
    } finally {
      setParsing(false);
    }
  };

  const handleMatchChange = (index: number, collaboratorId: string | null) => {
    setMatches((prev) =>
      prev.map((match, matchIndex) => {
        if (matchIndex !== index) return match;
        return {
          ...match,
          collaboratorId,
          status: collaboratorId ? 'manual' : 'not_found',
        };
      }),
    );
  };

  const handleImport = async () => {
    const competence = billingPeriod.trim();
    if (!competence) {
      showError('Informe a competência (AAAA-MM).');
      return;
    }
    const consolidated = await isCompetenceConsolidated(competence);
    if (consolidated) {
      showError('Não é possível importar: competência já consolidada.');
      return;
    }
    if (!matches.length) {
      showError('Realize o match dos colaboradores antes de salvar a importação.');
      return;
    }
    if (!user) {
      showError('É necessário estar autenticado para importar.');
      return;
    }
    setSubmitting(true);
    try {
      const recordsPreview = matches.map((match) => ({
        ...match.record,
        amount: perSeatAmount,
        collaboratorId: match.collaboratorId,
      }));
      const audit: HealthPromotionAuditItem[] = matches
        .map((match): HealthPromotionAuditItem | null => {
          if (match.status === 'not_found') {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              value: perSeatAmount,
              reason: 'Não vinculado a colaborador',
              type: 'not_found',
            };
          }
          const collaborator = collaboratorRefs.find((c) => c.id === match.collaboratorId);
          if (collaborator && !collaborator.isActive) {
            return {
              beneficiaryName: match.record.name,
              beneficiaryCpf: match.record.code,
              value: perSeatAmount,
              reason: 'Colaborador inativo',
              type: 'inactive',
            };
          }
          return null;
        })
        .filter((item): item is HealthPromotionAuditItem => item !== null);

      await createHealthPromotionImport(
        OPERATOR.slug,
        {
          billingPeriod: competence,
          totals: {
            dependents: 0,
            feeCount: matches.length,
            amount: perSeatAmount * matches.length,
          },
          billingValues: {
            collaborators: parseCurrencyValue(invoiceValue),
            dependents: 0,
          },
          sourceFiles,
          recordsPreview,
          audit,
        },
        user,
      );
      showSuccess('Importação salva com sucesso.');
      navigate('/expenses/benefits/health-promotion');
    } catch (error) {
      console.error('[HealthPromotionImportPage] failed to save import', error);
      showError('Não foi possível salvar a importação.');
    } finally {
      setSubmitting(false);
    }
  };

  const sortedMatches = useMemo(() => {
    const priority: Record<HealthPromotionMatchStatus, number> = {
      not_found: 0,
      manual: 1,
      auto_email: 2,
    };
    return matches
      .map((match, originalIndex) => ({ match, originalIndex }))
      .sort((a, b) => {
        const diff = priority[a.match.status] - priority[b.match.status];
        if (diff !== 0) return diff;
        const nameDiff = a.match.record.name.toLowerCase().localeCompare(b.match.record.name.toLowerCase(), 'pt-BR');
        if (nameDiff !== 0) return nameDiff;
        return a.originalIndex - b.originalIndex;
      });
  }, [matches]);

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits/health-promotion"
        icon={<TrophyIcon className="h-5 w-5" aria-hidden />}
        badge="Benefícios"
        title="Importar promoção a saúde"
        description="Selecione a operadora e envie o arquivo do período desejado."
      />

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Operadora</label>
            <select
              value={OPERATOR.slug}
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed"
            >
              <option value={OPERATOR.slug}>{OPERATOR.name}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Este fluxo suporta inicialmente a consolidação Wellhub.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Competência (AAAA-MM)</label>
            <input
              type="month"
              value={billingPeriod}
              onChange={(event) => setBillingPeriod(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              required={false}
            />
            <p className="mt-1 text-xs text-gray-500">Use o mês de referência presente no relatório exportado.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Valor fatura</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={invoiceValue}
              onChange={(event) => setInvoiceValue(event.target.value)}
              placeholder="0,00"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">Valor total a ser rateado entre os colaboradores ativos.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Arquivos</label>
            <label className="mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-primary-400">
              <span className="inline-flex items-center gap-2">
                <CloudArrowUpIcon className="h-5 w-5 text-primary-600" aria-hidden />
                {files.length ? `${files.length} arquivo(s) selecionado(s)` : 'Selecionar arquivos (.xlsx, .csv)'}
              </span>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" multiple onChange={handleFilesChange} />
            </label>
            <p className="mt-1 text-xs text-gray-500">Inclua planilhas e relatórios oficiais enviados pela Wellhub.</p>
            {files.length ? (
              <ul className="mt-2 space-y-1 text-xs text-gray-600">
                {files.map((file) => (
                  <li key={file.name} className="truncate">
                    {file.name}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={handleParse}
            disabled={!files.length || parsing}
            className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {parsing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Lendo arquivo...
              </>
            ) : (
              'Conferir'
            )}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={submitting || parsing || matches.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Salvando
              </>
            ) : (
              <>
                Importar
                <ArrowRightIcon className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>
        </div>
      </form>

      {parseError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div>
      ) : null}

      {sortedMatches.length > 0 ? (
        <div className="space-y-3 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Titulares (prévia)</h2>
              <p className="text-sm text-gray-600">
                Revise as inscrições importadas, confira totais e associe cada registro a um colaborador.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span>Encontrados: {sortedMatches.length}</span>
              <span>Assinaturas: {sortedMatches.length}</span>
              <span>Valor total: {formatCurrency(perSeatAmount * sortedMatches.length)}</span>
            </div>
          </div>

          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Titular</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Plano</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Identificador</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Valor</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Match</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaborador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {sortedMatches.map(({ match, originalIndex }) => (
                  <tr key={`${match.record.code}-${originalIndex}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{match.record.name}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-900">{match.record.planName}</td>
                    <td className="px-3 py-2 font-semibold text-gray-900 break-all">{match.record.code}</td>
                    <td className="px-3 py-2 text-gray-900">{formatCurrency(perSeatAmount)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={match.status} />
                    </td>
                    <td className="px-3 py-2">
                      <MappingCombobox
                        value={match.collaboratorId}
                        options={collaboratorRefs}
                        disabled={loadingCollaborators}
                        onChange={(id) => handleMatchChange(originalIndex, id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
